import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useAddTaskAssignee,
  useRemoveTaskAssignee,
  useAddTaskProject,
  useRemoveTaskProject,
  useAddTaskProjectGroup,
  useRemoveTaskProjectGroup,
  TaskWithRelations,
  TaskState,
} from "@/hooks/useTasks";
import { usePeople } from "@/hooks/usePeople";
import { useProjects } from "@/hooks/useProjects";
import { useProjectGroups } from "@/hooks/useProjectGroups";
import { formatProjectName } from "@/lib/format-project-name";
import { MultiSelect } from "@/components/tasks/MultiSelect";
import { Trash2 } from "lucide-react";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  state: z.enum(["backlog", "todo", "in_progress", "done"]),
  due_date: z.string().optional(),
  assigneeIds: z.array(z.string()),
  projectIds: z.array(z.string()),
  projectGroupIds: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskWithRelations | null;
  defaultAssigneeId?: string | null;
  defaultProjectId?: string | null;
  defaultState?: TaskState;
}

export function TaskDialog({
  open,
  onOpenChange,
  task,
  defaultAssigneeId,
  defaultProjectId,
  defaultState = "backlog",
}: TaskDialogProps) {
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const addAssignee = useAddTaskAssignee();
  const removeAssignee = useRemoveTaskAssignee();
  const addProject = useAddTaskProject();
  const removeProject = useRemoveTaskProject();
  const addProjectGroup = useAddTaskProjectGroup();
  const removeProjectGroup = useRemoveTaskProjectGroup();

  const { data: people } = usePeople("employee");
  const { data: projects } = useProjects();
  const { data: projectGroups } = useProjectGroups();
  const lastResetKeyRef = useRef<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      state: "backlog",
      due_date: "",
      assigneeIds: [],
      projectIds: [],
      projectGroupIds: [],
    },
  });

  const resetKey = task
    ? `edit:${task.id}`
    : `new:${defaultAssigneeId ?? ""}:${defaultProjectId ?? ""}:${defaultState}`;

  useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null;
      return;
    }
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;

    if (task) {
      form.reset({
        title: task.title,
        description: task.description || "",
        state: task.state as TaskState,
        due_date: task.due_date || "",
        assigneeIds: task.assignees.map((a) => a.person.id),
        projectIds: task.projects.map((p) => p.project.id),
        projectGroupIds: task.project_groups.map((pg) => pg.project_group.id),
      });
    } else {
      form.reset({
        title: "",
        description: "",
        state: defaultState,
        due_date: "",
        assigneeIds:
          defaultAssigneeId && defaultAssigneeId !== "unassigned"
            ? [defaultAssigneeId]
            : [],
        projectIds: defaultProjectId ? [defaultProjectId] : [],
        projectGroupIds: [],
      });
    }
  }, [
    task,
    form,
    open,
    resetKey,
    defaultAssigneeId,
    defaultProjectId,
    defaultState,
  ]);

  const handleSubmit = async (values: FormValues) => {
    if (task) {
      // Update existing task
      await updateTask.mutateAsync({
        id: task.id,
        title: values.title,
        description: values.description || null,
        state: values.state,
        due_date: values.due_date || null,
      });

      // Handle assignee changes
      const currentAssigneeIds = task.assignees.map((a) => a.person.id);
      const toAddAssignees = values.assigneeIds.filter(
        (id) => !currentAssigneeIds.includes(id),
      );
      const toRemoveAssignees = currentAssigneeIds.filter(
        (id) => !values.assigneeIds.includes(id),
      );

      for (const personId of toAddAssignees) {
        await addAssignee.mutateAsync({ taskId: task.id, personId });
      }
      for (const personId of toRemoveAssignees) {
        await removeAssignee.mutateAsync({ taskId: task.id, personId });
      }

      // Handle project changes
      const currentProjectIds = task.projects.map((p) => p.project.id);
      const toAddProjects = values.projectIds.filter(
        (id) => !currentProjectIds.includes(id),
      );
      const toRemoveProjects = currentProjectIds.filter(
        (id) => !values.projectIds.includes(id),
      );

      for (const projectId of toAddProjects) {
        await addProject.mutateAsync({ taskId: task.id, projectId });
      }
      for (const projectId of toRemoveProjects) {
        await removeProject.mutateAsync({ taskId: task.id, projectId });
      }

      // Handle project group changes
      const currentGroupIds = task.project_groups.map(
        (pg) => pg.project_group.id,
      );
      const toAddGroups = values.projectGroupIds.filter(
        (id) => !currentGroupIds.includes(id),
      );
      const toRemoveGroups = currentGroupIds.filter(
        (id) => !values.projectGroupIds.includes(id),
      );

      for (const projectGroupId of toAddGroups) {
        await addProjectGroup.mutateAsync({ taskId: task.id, projectGroupId });
      }
      for (const projectGroupId of toRemoveGroups) {
        await removeProjectGroup.mutateAsync({
          taskId: task.id,
          projectGroupId,
        });
      }
    } else {
      // Create new task
      const newTask = await createTask.mutateAsync({
        title: values.title,
        description: values.description || null,
        state: values.state,
        due_date: values.due_date || null,
      });

      // Add relations
      for (const personId of values.assigneeIds) {
        await addAssignee.mutateAsync({ taskId: newTask.id, personId });
      }
      for (const projectId of values.projectIds) {
        await addProject.mutateAsync({ taskId: newTask.id, projectId });
      }
      for (const projectGroupId of values.projectGroupIds) {
        await addProjectGroup.mutateAsync({
          taskId: newTask.id,
          projectGroupId,
        });
      }
    }

    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (task) {
      await deleteTask.mutateAsync(task.id);
      onOpenChange(false);
    }
  };

  const peopleOptions = (people || [])
    .filter((p) => !p.deleted_at)
    .map((p) => ({
      value: p.id,
      label: `${p.first_name} ${p.last_name}`,
    }));

  const projectOptions = (projects || [])
    .filter((p) => !p.deleted_at)
    .map((p) => ({
      value: p.id,
      label: formatProjectName(p.name, p.event_start_date),
    }));

  const projectGroupOptions = (projectGroups || [])
    .filter((pg) => !pg.deleted_at)
    .map((pg) => ({
      value: pg.id,
      label: pg.name,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="task-dialog"
      >
        <DialogHeader>
          <DialogTitle>{task ? "Edit Task" : "New Task"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={(e) => {
              // Submit form on Enter only from text inputs, not from Select/Command components
              if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
                e.preventDefault();
                form.handleSubmit(handleSubmit)();
              }
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="task-title-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      data-testid="task-description-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="task-state-select">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="backlog">Backlog</SelectItem>
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        data-testid="task-due-date-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="assigneeIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assignees</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={peopleOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select assignees..."
                      testId="task-assignees-select"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="projectIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Projects</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={projectOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select projects..."
                      testId="task-projects-select"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="projectGroupIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Groups</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={projectGroupOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select project groups..."
                      testId="task-project-groups-select"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between pt-4">
              {task && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  data-testid="task-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid="task-cancel-button"
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="task-submit-button">
                  {task ? "Save" : "Create"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
